import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, QrCode, RotateCcw, Share2 } from 'lucide-react';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { groupInitials, type GroupRole } from '../../../lib/group-chat';
import {
  adminErrorKey,
  clampDescription,
  descriptionCount,
  displayInviteLink,
  EMPTY_REPORT_FORM,
  GROUP_DESCRIPTION_MAX,
  linkStateFor,
  parseBlockedList,
  parseJoinRequests,
  REPORT_DETAILS_MAX,
  REPORT_REASON_KEYS,
  REPORT_REASONS,
  reportBody,
  reportFormValid,
  reportLeaveOffered,
  reportResult,
  resolveRequestResult,
  settingsChange,
  settingsSaveResult,
  updateSettingsBody,
  withoutBlocked,
  type BlockedPerson,
  type GroupJoinRequest,
  type GroupPermission,
  type GroupSettingKey,
  type GroupSettings,
  type LinkState,
  type ReportForm,
  type ReportReason,
  type ReportTarget,
} from '../../../lib/group-admin';
import { dropGroupConversation, groupChatAction, handleLostAccess, showGroupNotice } from '../../../lib/group-chat-api';
import { queryClient } from '../../../lib/query-client';
import { useObservedQuery } from '../../../lib/use-observed-query';
import { fetchGroupMembers, groupMembersKey } from '../../../lib/use-group-summary';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Spinner } from '../../Spinner';
import { Avatar, Button, RadioGroup, Switch, Typography } from '../ui';
import { GroupConfirmDialog } from './GroupChatDialogs';

/**
 * Group chat Phase 10a, web's dialogs (spec items 2–4, 7, 8): group settings, invite via link, join requests, the
 * report form and the blocked list. The phones draw the same as sheets. Every rule is in `lib/group-admin.ts`; these
 * components move data and draw. A transport failure never leaves a spinner turning and never loses state.
 */

const errorStyle = { color: 'var(--ds-text-destructive)', marginTop: 8 } as const;
const textareaStyle = {
  width: '100%',
  minHeight: 96,
  boxSizing: 'border-box',
  padding: 12,
  borderRadius: 8,
  border: '1px solid var(--ds-input-border-outline-default)',
  background: 'transparent',
  color: 'var(--ds-text-primary)',
  font: 'inherit',
  fontSize: 14,
  lineHeight: '20px',
  resize: 'vertical',
} as const;

const bi = (log: ReturnType<typeof useGenericBi>['logPageClickBi'], button: string) => log({ page_name: 'chat', button_name: button });

// ─── Group settings (admins) ─────────────────────────────────────────────────────────────────────────────────

export function GroupSettingsDialog({ conversationId, settings: initial, onClose }: { conversationId: string; settings: GroupSettings; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [settings, setSettings] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Every save sends ALL four values, so nothing is sent until a FRESH `members` read answers the group's settings: a
  // cached read may be another admin's change behind, and a never-edited group sits at revision 0, so the revision
  // check does not stop an old or default copy from overwriting what the admin did not touch (2026-11-01). The
  // controls stay disabled until then; a failed read keeps them disabled with the error line. iOS/Android the same.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    fetchGroupMembers(conversationId)
      .then((read) => {
        if (!live) return;
        queryClient.setQueryData(groupMembersKey(conversationId), read);
        if (read.details) {
          setSettings(read.details.settings);
          setLoaded(true);
        } else {
          setMessage('chat.group.maestro.loadFailed');
        }
      })
      .catch(() => live && setMessage('chat.group.maestro.loadFailed'));
    return () => {
      live = false;
    };
  }, [conversationId]);

  /** Saves at once with all four values and the revision last read; optimistic, reverted on failure. */
  const change = async <K extends GroupSettingKey>(key: K, value: GroupSettings[K]) => {
    if (!loaded || saving || settings[key] === value) return;
    bi(logPageClickBi, 'group_settings_change');
    const before = settings;
    const draft = settingsChange(before, key, value);
    setSettings(draft);
    setSaving(true);
    setMessage(null);
    const outcome = await groupChatAction('update-settings', updateSettingsBody(conversationId, draft));
    setSaving(false);
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    const result = settingsSaveResult(before, draft, outcome);
    setSettings(result.settings);
    setMessage(result.messageKey);
    if (result.details) void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
  };

  const choice = (key: Exclude<GroupSettingKey, 'joinApproval'>, labelKey: string, hintKey?: string) => (
    <RadioGroup<GroupPermission>
      label={t(labelKey)}
      value={settings[key]}
      disabled={saving || !loaded}
      onChange={(next) => void change(key, next)}
      options={[
        { value: 'ALL_MEMBERS', label: t('chat.group.settings.allMembers') },
        { value: 'ADMINS_ONLY', label: t('chat.group.settings.adminsOnly') },
      ]}
      {...(hintKey ? { info: t(hintKey) } : {})}
    />
  );

  return (
    <Modal open onClose={onClose} title={t('chat.group.settings.title')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
      <div data-testid="group-settings" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {choice('whoCanSendMessages', 'chat.group.settings.whoCanSend')}
        <div>
          {choice('whoCanEditInfo', 'chat.group.settings.whoCanEdit')}
          <Typography variant="p3" color="secondary" component="p">{t('chat.group.settings.whoCanEditHint')}</Typography>
        </div>
        <div>
          {choice('whoCanAddPeople', 'chat.group.settings.whoCanAdd')}
          <Typography variant="p3" color="secondary" component="p">{t('chat.group.settings.whoCanAddHint')}</Typography>
        </div>
        <Switch
          checked={settings.joinApproval}
          disabled={saving || !loaded}
          onChange={(next) => void change('joinApproval', next)}
          label={t('chat.group.settings.joinApproval')}
          description={t('chat.group.settings.joinApprovalHint')}
        />
        {message && <Typography variant="p2" component="p" role="alert" style={errorStyle}>{t(message)}</Typography>}
      </div>
    </Modal>
  );
}

// ─── Invite via link ─────────────────────────────────────────────────────────────────────────────────────────

export function InviteLinkDialog({ conversationId, canReset, onClose }: { conversationId: string; canReset: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<LinkState>({ kind: 'loading' });
  const [qr, setQr] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const load = async () => {
    setState({ kind: 'loading' });
    const outcome = await groupChatAction('invite-link', { conversationId });
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    setState(linkStateFor(outcome));
  };
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const link = state.kind === 'shown' ? state.link : null;
  const copy = async () => {
    if (!link) return;
    bi(logPageClickBi, 'group_link_copy');
    try {
      await navigator.clipboard.writeText(link.url);
      showGroupNotice('chat.group.linkCopied');
    } catch {
      showGroupNotice('chat.group.error.retryLater');
    }
  };
  const share = async () => {
    if (!link) return;
    bi(logPageClickBi, 'group_link_share');
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: t('chat.group.link.shareMessage', { url: link.url }) });
      } catch {
        // the reader closed the share sheet
      }
      return;
    }
    await copy();
  };
  const reset = async () => {
    bi(logPageClickBi, 'group_link_reset');
    setBusy(true);
    setResetError(null);
    const outcome = await groupChatAction('reset-invite-link', { conversationId });
    setBusy(false);
    const next = linkStateFor(outcome, { kind: 'loading' });
    if (next.kind === 'shown') {
      setState(next);
      setConfirmReset(false);
      showGroupNotice('chat.group.link.resetDone');
      return;
    }
    setResetError(t(adminErrorKey(outcome)));
  };

  if (confirmReset) {
    return (
      <GroupConfirmDialog title={t('chat.group.link.resetTitle')} body={t('chat.group.link.resetBody')} confirmLabel={t('chat.group.link.reset')}
        busy={busy} error={resetError} onClose={() => setConfirmReset(false)} onConfirm={() => void reset()} />
    );
  }

  return (
    <Modal open onClose={onClose} title={t('chat.group.link.title')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
      <div data-testid="group-invite-link" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {state.kind === 'loading' && <Spinner size={20} />}
        {state.kind === 'error' && (
          <>
            <Typography variant="p2" component="p" role="alert" style={errorStyle}>{t(state.messageKey)}</Typography>
            <Button variant="outlined" onClick={() => void load()}>{t('common.retry')}</Button>
          </>
        )}
        {link && (
          <>
            {/* A short readable link on ONE line (`displayInviteLink`); Copy / Share / QR use the full one. */}
            <div data-testid="group-invite-link-url" title={link.url} aria-label={t('chat.group.link.title')} dir="ltr"
              style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--ds-surface-sunken)', fontSize: 14, lineHeight: '20px', color: 'var(--ds-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'all' }}>
              {displayInviteLink(link.url)}
            </div>
            <Typography variant="p3" color="secondary" component="p">{t('chat.group.link.hint')}</Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Button variant="outlined" startIcon={<Copy size={16} aria-hidden />} onClick={() => void copy()}>{t('chat.group.copyLink')}</Button>
              <Button variant="outlined" startIcon={<Share2 size={16} aria-hidden />} onClick={() => void share()}>{t('chat.group.shareLink')}</Button>
              <Button variant="outlined" startIcon={<QrCode size={16} aria-hidden />} onClick={() => { bi(logPageClickBi, 'group_link_qr'); setQr((v) => !v); }}>{t('chat.group.link.qr')}</Button>
              {canReset && (
                <Button variant="outlined" color="destructive" startIcon={<RotateCcw size={16} aria-hidden />} onClick={() => { setResetError(null); setConfirmReset(true); }}>
                  {t('chat.group.link.reset')}
                </Button>
              )}
            </div>
            {qr && (
              <div data-testid="group-invite-qr" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {/* The library's own black-on-white with its quiet zone, in both themes: a QR is a machine target. */}
                <QRCodeSVG value={link.qrUrl} size={192} level="M" marginSize={4} title={t('chat.group.link.qr')} />
                <Typography variant="p3" color="secondary" component="p" align="center">{t('chat.group.link.qrHint')}</Typography>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Join requests (admins) ──────────────────────────────────────────────────────────────────────────────────

export const joinRequestsKey = (conversationId: string) => ['group-join-requests', conversationId] as const;

export async function fetchJoinRequests(conversationId: string): Promise<GroupJoinRequest[]> {
  const outcome = await groupChatAction('join-requests', { conversationId });
  if (outcome.kind === 'refused') {
    handleLostAccess(conversationId, outcome.refusal);
    throw new Error(outcome.refusal);
  }
  if (outcome.kind !== 'ok') throw new Error('join_requests_failed');
  return parseJoinRequests(outcome.data.requests);
}

export function useJoinRequests(conversationId: string | null | undefined, enabled: boolean) {
  return useObservedQuery({
    queryKey: joinRequestsKey(conversationId ?? ''),
    queryFn: () => fetchJoinRequests(conversationId as string),
    enabled: enabled && !!conversationId,
    staleTime: 15_000,
    retry: false,
  });
}

/** The list itself — drawn in the dialog inside the chat and on the `/group-join-requests/<id>` screen. */
export function JoinRequestsList({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const query = useJoinRequests(conversationId, true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolve = async (request: GroupJoinRequest, approve: boolean) => {
    if (busyId) return;
    bi(logPageClickBi, approve ? 'group_request_approve' : 'group_request_deny');
    setBusyId(request.requestId);
    setError(null);
    const outcome = await groupChatAction('resolve-join-request', { conversationId, requestId: request.requestId, approve });
    setBusyId(null);
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return;
    const result = resolveRequestResult(outcome, approve, request.name);
    if (result.kind === 'error') {
      setError(t(result.messageKey));
      return;
    }
    queryClient.setQueryData<GroupJoinRequest[]>(joinRequestsKey(conversationId), (rows) => (rows ?? []).filter((r) => r.requestId !== request.requestId));
    if (result.kind === 'resolved') {
      showGroupNotice(result.toastKey, result.vars);
      if (approve) void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
    } else {
      setError(t(result.messageKey));
    }
  };

  if (query.isLoading) return <Spinner size={20} />;
  if (query.isError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <Typography variant="p2" component="p" role="alert" style={errorStyle}>{t('chat.group.requests.loadFailed')}</Typography>
        <Button variant="outlined" onClick={() => void query.refetch()}>{t('common.retry')}</Button>
      </div>
    );
  }
  const rows = query.data ?? [];
  return (
    <div data-testid="group-join-requests">
      {rows.length === 0 ? (
        <Typography variant="p2" color="secondary" component="p">{t('chat.group.requests.empty')}</Typography>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((r) => (
            <li key={r.requestId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
              <Avatar size={40} initials={groupInitials(r.name)} photoUrl={r.avatarUrl} color={avatarSlotFor(r.metaSiteId ?? r.requestId)} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              <Button size="small" variant="outlined" disabled={!!busyId} onClick={() => void resolve(r, false)}>{t('chat.group.approval.deny')}</Button>
              <Button size="small" loading={busyId === r.requestId} disabled={!!busyId && busyId !== r.requestId} onClick={() => void resolve(r, true)}>{t('chat.group.approval.approve')}</Button>
            </li>
          ))}
        </ul>
      )}
      {error && <Typography variant="p2" component="p" role="alert" style={errorStyle}>{error}</Typography>}
    </div>
  );
}

export function JoinRequestsDialog({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal open onClose={onClose} title={t('chat.group.requests.title')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
      <JoinRequestsList conversationId={conversationId} />
    </Modal>
  );
}

// ─── Report a group or a message ─────────────────────────────────────────────────────────────────────────────

export function ReportDialog({ conversationId, target, role, onClose }: { conversationId: string; target: ReportTarget; role: GroupRole | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [form, setForm] = useState<ReportForm>(EMPTY_REPORT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const request = reportBody(conversationId, target, form, role);
    if (!request || busy) return;
    bi(logPageClickBi, target.kind === 'group' ? 'group_report' : 'group_message_report');
    setBusy(true);
    setError(null);
    const outcome = await groupChatAction(request.action, request.body);
    setBusy(false);
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    const result = reportResult(outcome);
    if (result.kind === 'error') {
      setError(t(result.messageKey));
      return;
    }
    onClose();
    // Reported and left: exactly a Leave (the row and transcript cleared, the chat closed), with the report's toast.
    if (result.left) dropGroupConversation(conversationId, 'chat.group.report.done');
    else showGroupNotice('chat.group.report.done');
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(target.kind === 'group' ? 'chat.group.report.groupTitle' : 'chat.group.report.messageTitle')}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      // The details field raises the keyboard: Report must stay visible above it on a phone (2026-10-31 rerun, D3).
      keyboardAware
      footer={
        <>
          <Button variant="outlined" disabled={busy} onClick={onClose}>{t('chat.group.cancel')}</Button>
          <Button color="destructive" loading={busy} disabled={!reportFormValid(form)} onClick={() => void submit()} data-testid="group-report-submit">
            {t('chat.group.report.submit')}
          </Button>
        </>
      }
    >
      <div data-testid="group-report" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Typography variant="p2" color="secondary" component="p">{t('chat.group.report.body')}</Typography>
        <RadioGroup<ReportReason>
          label={t('chat.group.report.reasonLabel')}
          value={form.reason}
          disabled={busy}
          onChange={(reason) => setForm((f) => ({ ...f, reason }))}
          options={REPORT_REASONS.map((r) => ({ value: r, label: t(REPORT_REASON_KEYS[r]) }))}
        />
        <textarea
          aria-label={t('chat.group.report.detailsPlaceholder')}
          placeholder={t('chat.group.report.detailsPlaceholder')}
          value={form.details}
          maxLength={REPORT_DETAILS_MAX}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
          style={textareaStyle}
        />
        {reportLeaveOffered(role) && (
          <Switch checked={form.leave} disabled={busy} onChange={(leave) => setForm((f) => ({ ...f, leave }))} label={t('chat.group.report.alsoLeave')} />
        )}
        {error && <Typography variant="p2" component="p" role="alert" style={errorStyle}>{error}</Typography>}
      </div>
    </Modal>
  );
}

// ─── Description ─────────────────────────────────────────────────────────────────────────────────────────────

export function GroupDescriptionDialog({ initial, busy, error, onSave, onClose }: { initial: string; busy: boolean; error: string | null; onSave: (text: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={t('chat.group.info.editDescription')}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={busy}>{t('chat.group.cancel')}</Button>
          <Button loading={busy} onClick={() => onSave(value)} data-testid="group-description-save">{t('chat.group.save')}</Button>
        </>
      }>
      <textarea
        aria-label={t('chat.group.info.description')}
        placeholder={t('chat.group.info.descriptionPlaceholder')}
        value={value}
        maxLength={GROUP_DESCRIPTION_MAX}
        onChange={(e) => setValue(clampDescription(e.target.value))}
        style={{ ...textareaStyle, minHeight: 140 }}
      />
      <Typography variant="p3" color="secondary" component="p" align="end">
        {t('chat.group.info.charCount', { count: descriptionCount(value), max: GROUP_DESCRIPTION_MAX })}
      </Typography>
      {error && <Typography variant="p2" component="p" role="alert" style={errorStyle}>{error}</Typography>}
    </Modal>
  );
}

// ─── Blocked people (Settings) ───────────────────────────────────────────────────────────────────────────────

export const blockedKey = ['group-blocked'] as const;

export async function fetchBlocked(): Promise<BlockedPerson[]> {
  const outcome = await groupChatAction('blocked', {});
  if (outcome.kind !== 'ok') throw new Error('blocked_failed');
  return parseBlockedList(outcome.data.blocked);
}

export function useBlockedPeople(enabled: boolean) {
  return useObservedQuery({ queryKey: blockedKey, queryFn: fetchBlocked, enabled, staleTime: 30_000, retry: false });
}

/** Unblock one person: immediate, its toast; a failure leaves the list as it was. True when it unblocked. */
export async function unblockPerson(person: Pick<BlockedPerson, 'metaSiteId' | 'name'>): Promise<boolean> {
  const outcome = await groupChatAction('unblock', { metaSiteId: person.metaSiteId });
  if (outcome.kind !== 'ok') {
    showGroupNotice(outcome.kind === 'refused' ? adminErrorKey(outcome) : 'chat.group.block.failed');
    return false;
  }
  queryClient.setQueryData<BlockedPerson[]>(blockedKey, (list) => withoutBlocked(list ?? [], person.metaSiteId));
  showGroupNotice('chat.group.block.unblocked', { name: person.name });
  return true;
}

export function BlockedPeopleDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const query = useBlockedPeople(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const rows = query.data ?? [];
  return (
    <Modal open onClose={onClose} title={t('chat.group.block.listTitle')} size="minimum" maxHeightPx={650} scrollBody headerSeparator>
      <div data-testid="group-blocked-list">
        {query.isLoading ? (
          <Spinner size={20} />
        ) : query.isError ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <Typography variant="p2" component="p" role="alert" style={errorStyle}>{t('chat.group.block.loadFailed')}</Typography>
            <Button variant="outlined" onClick={() => void query.refetch()}>{t('common.retry')}</Button>
          </div>
        ) : rows.length === 0 ? (
          <Typography variant="p2" color="secondary" component="p">{t('chat.group.block.listEmpty')}</Typography>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((p) => (
              <li key={p.metaSiteId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                <Avatar size={40} initials={groupInitials(p.name)} photoUrl={p.avatarUrl} color={avatarSlotFor(p.metaSiteId)} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: '18px', color: 'var(--ds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <Button size="small" variant="outlined" loading={busyId === p.metaSiteId} disabled={!!busyId}
                  onClick={async () => {
                    bi(logPageClickBi, 'person_unblock');
                    setBusyId(p.metaSiteId);
                    await unblockPerson(p);
                    setBusyId(null);
                  }}>
                  {t('chat.group.block.unblock')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
